import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../../../../environment/environment';
import { CanDeactivateType } from '../../../../../guards/form.guard';
import {
  CooperationCreateUpdateModel,
  CooperationModel,
} from '../../../../../models/cooperation.model';
import { NoteModel } from '../../../../../models/note.model';
import {
  InitialModel,
  LecturerCreateModel,
  LecturerUpdateModel,
  UserModel,
  UserType,
  UserUpdateModel,
} from '../../../../../models/user.model';
import { AlertService } from '../../../../../services/alert.service';
import { CooperationService } from '../../../../../services/cooperation.service';
import { FormService } from '../../../../../services/form.service';
import { UserService } from '../../../../../services/user.service';
import { getRandomAvatarColorStyles } from '../../../../../utils/color.utils';
import {
  base64ToFile,
  resizeAndPadImage,
} from '../../../../../utils/file.utils';
import {
  fieldRequired,
  getTextAreaLength,
  hasUnsavedChanges,
} from '../../../../../utils/form.utils';
import { translateUserType } from '../../../../../utils/language.utils';
import {
  getUserFullName,
  getUserInitials,
} from '../../../../../utils/user.utils';
import { ConfirmDialogComponent } from '../../../../shared-components/confirm-dialog/confirm-dialog.component';
import { ImageCropperDialogComponent } from '../../../../shared-components/image-cropper-dialog/image-cropper-dialog.component';
import { NoteDialogComponent } from '../../../../shared-components/note-dialog/note-dialog.component';
import { TabMenuTabModel } from '../../../../shared-components/tab-menu/tab-menu.component';

interface ExtendedNoteModel extends NoteModel {
  userInitials: InitialModel;
}

enum ContactTabEnum {
  PROFILE = 'profile',
  BOOKINGS = 'bookings',
  APPOINTMENTS = 'appointments',
  MEMBER_AREAS = 'member-areas',
  NOTES = 'notes',
}

@Component({
  selector: 'app-contacts-formular',
  templateUrl: './contacts-formular.component.html',
  styleUrl: './contacts-formular.component.scss',
  standalone: false,
})
export class ContactsFormularComponent implements OnInit, OnDestroy {
  public isLoading = false;
  public editMode = false;
  public user: UserModel;
  public activeTab: ContactTabEnum = ContactTabEnum.PROFILE;
  public userForm: FormGroup;
  public notes: ExtendedNoteModel[];
  public selectedNote: NoteModel;
  public avatarStyles: string;
  public userType: UserType;
  public tabs: TabMenuTabModel[] = [];
  public showVita = false;
  public userId: number;
  public selectedImage: string | null = null;
  public UserType = UserType;

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

  private initialFormValues: any;

  public noteMenuItems = [
    {
      label: 'Bearbeiten',
      icon: 'fa-regular fa-pen',
      command: () => {
        this.updateNote();
      },
    },
    {
      label: 'Löschen',
      icon: 'fa-regular fa-trash-can',
      command: () => {
        this.deleteNote();
      },
    },
  ];

  destroy$ = new Subject<void>();

  /* add window.onbeforeunload to warn the user if the form has unsaved changes */
  @HostListener('window:beforeunload', ['$event'])
  public reloadNotification($event: any): void {
    if (hasUnsavedChanges(this.initialFormValues, this.userForm.value)) {
      $event.returnValue =
        'Es gibt ungespeicherte Änderungen. Wenn Sie die Seite verlassen, gehen Daten verloren.';
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private dialogService: DialogService,
    private formService: FormService,
    private alertService: AlertService,
    private cooperationService: CooperationService
  ) {
    this.createForm();
    const randomAvatarStyles = getRandomAvatarColorStyles();
    this.avatarStyles = `background-color: ${randomAvatarStyles.background}; color: ${randomAvatarStyles.font}`;
  }

  public getUserInitials = getUserInitials;
  public getUserFullName = getUserFullName;
  public fieldRequired = fieldRequired;
  public getTextAreaLength = getTextAreaLength;
  public translateUserType = translateUserType;

  public avatarLoaded: boolean = false;

  async ngOnInit(): Promise<void> {
    // check for query params to open specific tab
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['tab']) {
        this.activeTab = params['tab'];
      }
      if (params['type']) {
        this.userType = params['type'];
        this.initTabs();
      }
    });
    if (this.userType === UserType.COOPERATION) {
      this.addSponsorCard();
    }
    const userId = this.route.snapshot.params['userId'];
    if (userId) {
      this.editMode = true;
      this.isLoading = true;
      this.userId = +atob(userId);
      await this.fetchUser(this.userId, this.userType);
      await this.fetchUserNotes(this.userId);
      this.isLoading = false;
    }

    this.initialFormValues = JSON.parse(JSON.stringify(this.userForm.value));
  }

  public get avatar(): string | File {
    if (this.selectedImage) {
      return this.selectedImage;
    }
    if (this.user?.avatarPath) {
      return environment.authority_short + this.user.avatarPath;
    }
    return null;
  }

  public getInitials(): string {
    if (this.userType === UserType.COOPERATION) {
      return this.userForm.value.cooperationName.charAt(0);
    }
    var initials = '';
    if (this.userForm.value.firstname) {
      initials += this.userForm.value.firstname.charAt(0);
    }
    if (this.userForm.value.lastname) {
      initials += this.userForm.value.lastname.charAt(0);
    }
    return initials;
  }

  private initTabs(): void {
    if (this.userType === UserType.LECTURER) {
      this.tabs = [
        {
          key: 'profile',
          label: 'Dozent Profil',
          iconClass: 'fa-chalkboard-teacher',
        },
        {
          key: 'notes',
          label: 'Notizen',
          iconClass: 'fa-pen',
        },
      ];
    } else if (this.userType === UserType.COOPERATION) {
      this.tabs = [
        {
          key: 'profile',
          label: 'Kontaktdaten',
          iconClass: 'fa-user',
        },
      ];
    } else {
      this.tabs = [
        {
          key: 'profile',
          label: 'Nutzerdaten',
          iconClass: 'fa-user',
        },
        {
          key: 'bookings',
          label: 'Buchungen',
          iconClass: 'fa-book-open-cover',
        },
        {
          key: 'appointments',
          label: 'Termine',
          iconClass: 'fa-calendar-heart',
        },
        {
          key: 'member-areas',
          label: 'Mitgliederbereiche',
          iconClass: 'fa-folders',
        },
        {
          key: 'notes',
          label: 'Notizen',
          iconClass: 'fa-pen',
        },
      ];
    }
  }

  private createForm(): void {
    this.userForm = new FormGroup({
      jobTitle: new FormControl(''),
      cooperationName: new FormControl(''),
      firstname: new FormControl('', [Validators.required]),
      lastname: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      avatar: new FormControl(null),
      phone: new FormControl(''),
      description1: new FormControl('', [Validators.maxLength(1200)]),
      description2: new FormControl('', [Validators.maxLength(1200)]),
      description3: new FormControl('', [Validators.maxLength(1200)]),
      fieldOfActivity: new FormControl('', [Validators.maxLength(1200)]),
      trainings: new FormArray([
        new FormControl(null, [Validators.maxLength(1200)]),
      ]),
      careerSteps: new FormArray([
        new FormControl(null, [Validators.maxLength(1200)]),
      ]),
      publications: new FormArray([
        new FormGroup({
          title: new FormControl(null, [Validators.maxLength(1200)]),
          link: new FormControl(null, [Validators.maxLength(1200)]),
        }),
      ]),
      engagements: new FormArray([
        new FormControl(null, [Validators.maxLength(1200)]),
      ]),
      contributions: new FormArray([
        new FormControl(null, [Validators.maxLength(1200)]),
      ]),
      sponsorCards: new FormArray([]),
    });
  }

  get trainings(): FormArray {
    return this.userForm.get('trainings') as FormArray;
  }

  get careerSteps(): FormArray {
    return this.userForm.get('careerSteps') as FormArray;
  }

  get publications(): FormArray {
    return this.userForm.get('publications') as FormArray;
  }

  get engagements(): FormArray {
    return this.userForm.get('engagements') as FormArray;
  }

  get contributions(): FormArray {
    return this.userForm.get('contributions') as FormArray;
  }

  get sponsorCards(): FormArray {
    return this.userForm.get('sponsorCards') as FormArray;
  }

  public addTraining(): void {
    this.trainings.push(new FormControl(null, [Validators.maxLength(1200)]));
  }

  public addCareerStep(): void {
    this.careerSteps.push(new FormControl(null, [Validators.maxLength(1200)]));
  }

  public addPublication(): void {
    this.publications.push(
      new FormGroup({
        title: new FormControl(null, [Validators.maxLength(1200)]),
        link: new FormControl(null, [Validators.maxLength(1200)]),
      })
    );
  }

  public addEngagement(): void {
    this.engagements.push(new FormControl(null, [Validators.maxLength(1200)]));
  }

  public addContribution(): void {
    this.contributions.push(
      new FormControl(null, [Validators.maxLength(1200)])
    );
  }

  public addSponsorCard(): void {
    this.sponsorCards.push(
      new FormGroup({
        title: new FormControl(null, [Validators.maxLength(100)]),
        description: new FormControl(null, [Validators.maxLength(250)]),
        image: new FormControl(null),
        selectedImage: new FormControl(null),
        link: new FormControl(null),
        active: new FormControl(true),
      })
    );
  }

  public removeSponsorCard(index: number): void {
    const sponsorCardId = this.sponsorCards.at(index).get('id').value;
    if (!sponsorCardId) {
      this.sponsorCards.removeAt(index);
      return;
    }

    this.isLoading = true;

    this.cooperationService
      .checkSponsorCardDeletion(sponsorCardId)
      .pipe(take(1))
      .subscribe({
        next: canDelete => {
          this.isLoading = false;
          if (canDelete) {
            this.sponsorCards.removeAt(index);
          } else {
            this.alertService.showErrorAlert(
              'Die Sponsor Card kann nicht gelöscht werden, da sie noch in Verwendung ist.'
            );
          }
        },
        error: error => {
          this.isLoading = false;
          if (error.error === 'still_in_use') {
            this.alertService.showErrorAlert(
              'Die Sponsor Card kann nicht gelöscht werden, da sie noch in Verwendung ist.'
            );
            return;
          }
          this.alertService.showErrorAlert(
            'Bei der Überprüfung der Löschung ist ein Fehler aufgetreten.'
          );
        },
      });
  }

  public addNote(): void {
    const dialogRef = this.dialogService.open(NoteDialogComponent, {
      width: '585px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Notiz hinzufügen',
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((data: string) => {
      const note: ExtendedNoteModel = {
        id: 99,
        text: data,
        createdAt: new Date(),
        user: this.userService.getUser(),
        userInitials: getUserInitials(this.userService.getUser()),
      };
      if (data) {
        this.notes.push(note);
        // add backend call
      }
    });
  }

  public updateNote(): void {
    const dialogRef = this.dialogService.open(NoteDialogComponent, {
      width: '585px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Notiz bearbeiten',
        note: this.selectedNote.text,
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((data: string) => {
      if (data) {
        this.selectedNote.text = data;
        // add backend call
      }
    });
  }

  public deleteNote(): void {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Notiz löschen',
        message:
          'Bist du sicher, dass Sie die Notiz dieses Benutzers löschen möchten?',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
      },
    });
    dialogRef.onClose.pipe(take(1)).subscribe((result: boolean) => {
      if (result) {
        this.notes = this.notes.filter(
          note => note.id !== this.selectedNote.id
        );
        // add backend call
      }
    });
  }

  public toggleDetails(): void {
    if (!this.showVita) {
      this.updateUser();
    }
    this.showVita = !this.showVita;
  }

  private updateUser(): void {
    this.user = {
      ...this.user,
      jobTitle: this.userForm.value.jobTitle,
      firstname: this.userForm.value.firstname,
      lastname: this.userForm.value.lastname,
      email: this.userForm.value.email,
      phoneNumber: this.userForm.value.phone,
      description1: this.userForm.value.description1,
      description2: this.userForm.value.description2,
      description3: this.userForm.value.description3,
      fieldOfActivity: this.userForm.value.fieldOfActivity,
      trainings: this.userForm.value.trainings.filter(training => !!training),
      careerSteps: this.userForm.value.careerSteps.filter(
        careerStep => !!careerStep
      ),
      publications: this.userForm.value.publications.filter(
        publication => !!publication.title
      ),
      engagements: this.userForm.value.engagements.filter(
        engagement => !!engagement
      ),
      contributions: this.userForm.value.contributions.filter(
        contribution => !!contribution
      ),
      avatar: this.selectedImage,
    };
  }

  private async fetchUser(userId: number, userType: UserType): Promise<void> {
    if (userType === UserType.COOPERATION) {
      return new Promise((resolve, reject) => {
        this.cooperationService
          .getCooperationById(userId)
          .pipe(take(1))
          .subscribe({
            next: cooperation => {
              this.user = cooperation;
              this.patchCooperationFormValues(cooperation);
              resolve();
            },
            error: error => {
              console.error('Error fetching user', error);
              this.alertService.showErrorAlert(
                'Die Daten des Benutzers konnten nicht geladen werden.'
              );
              reject();
            },
          });
      });
    }
    return new Promise((resolve, reject) => {
      this.userService
        .getUserById(userId)
        .pipe(take(1))
        .subscribe({
          next: user => {
            this.user = user;
            this.patchFormValues();
            resolve();
          },
          error: error => {
            console.error('Error fetching user', error);
            this.alertService.showErrorAlert(
              'Die Daten des Benutzers konnten nicht geladen werden.'
            );
            reject();
          },
        });
    });
  }

  private patchCooperationFormValues(cooperation: CooperationModel): void {
    this.userForm.patchValue({
      cooperationName: cooperation.cooperationName,
      firstname: cooperation.firstname,
      lastname: cooperation.lastname,
      email: cooperation.email,
      phone: cooperation.phoneNumber,
    });
    this.sponsorCards.clear();
    cooperation.sponsorCards.forEach(sponsorCard => {
      this.sponsorCards.push(
        new FormGroup({
          id: new FormControl(sponsorCard.id),
          title: new FormControl(sponsorCard.title ?? '', [
            Validators.maxLength(100),
          ]),
          description: new FormControl(sponsorCard.description ?? '', [
            Validators.maxLength(250),
          ]),
          image: new FormControl(null),
          selectedImage: new FormControl(sponsorCard.image),
          link: new FormControl(sponsorCard.link ?? ''),
          active: new FormControl(sponsorCard.active !== false),
        })
      );
    });
  }

  private patchFormValues(): void {
    this.userForm.patchValue({
      jobTitle: this.user.jobTitle,
      cooperationName: this.user.cooperationName,
      firstname: this.user.firstname,
      lastname: this.user.lastname,
      email: this.user.email,
      phone: this.user.phoneNumber,
      description1: this.user.description1,
      description2: this.user.description2,
      description3: this.user.description3,
      fieldOfActivity: this.user.fieldOfActivity,
    });
    if (this.user.trainings && this.user.trainings.length > 0) {
      this.trainings.clear();
      this.user.trainings.forEach(training => {
        this.trainings.push(
          new FormControl(training ?? '', [Validators.maxLength(1200)])
        );
      });
    }
    if (this.user.careerSteps && this.user.careerSteps.length > 0) {
      this.careerSteps.clear();
      this.user.careerSteps.forEach(careerStep => {
        this.careerSteps.push(
          new FormControl(careerStep ?? '', [Validators.maxLength(1200)])
        );
      });
    }
    if (this.user.publications && this.user.publications.length > 0) {
      this.publications.clear();
      this.user.publications.forEach(publication => {
        this.publications.push(
          new FormGroup({
            title: new FormControl(publication.title ?? '', [
              Validators.maxLength(1200),
            ]),
            link: new FormControl(publication.link ?? '', [
              Validators.maxLength(1200),
            ]),
          })
        );
      });
    }
    if (this.user.engagements && this.user.engagements.length > 0) {
      this.engagements.clear();
      this.user.engagements.forEach(engagement => {
        this.engagements.push(
          new FormControl(engagement ?? '', [Validators.maxLength(1200)])
        );
      });
    }
    if (this.user.contributions && this.user.contributions.length > 0) {
      this.contributions.clear();
      this.user.contributions.forEach(contribution => {
        this.contributions.push(
          new FormControl(contribution ?? '', [Validators.maxLength(1200)])
        );
      });
    }
  }

  public onDeleteUser(): void {
    // check if lecturer has any courses
    // if yes, show info that lecturer must be removed from courses first
    // if no, show delete confirmation dialog
    if (this.user.type === UserType.LECTURER) {
      // check if lecturer has any courses
      // if yes, show info that lecturer must be removed from courses first
      // if no, proceed with delete
    }
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title:
          this.userType === UserType.COOPERATION
            ? 'Kooperationspartner löschen'
            : 'Benutzer löschen',
        message:
          this.userType === UserType.COOPERATION
            ? 'Bist du sicher, dass du diesen Kooperationspartner mit all seinen Sponsor Cards löschen willst?'
            : 'Bist du sicher, dass du diesen Benutzer löschen möchten?',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
      },
    });
    dialogRef.onClose.pipe(take(1)).subscribe((result: boolean) => {
      if (result) {
        // add backend call
        if (this.userType === UserType.COOPERATION) {
          this.cooperationService.deleteCooperation(this.user.id).subscribe({
            next: () => {
              this.alertService.showSuccessAlert(
                'Kooperation erfolgreich gelöscht.'
              );
              this.router.navigate(['../../'], { relativeTo: this.route });
            },
            error: error => {
              if (error.error === 'still_in_use') {
                this.alertService.showErrorAlert(
                  'Die Kooperation kann nicht gelöscht werden, da sie noch in Verwendung ist.'
                );
                return;
              }
              this.alertService.showErrorAlert(
                'Fehler beim Löschen der Kooperation.'
              );
            },
          });
        }
      }
    });
  }

  public onSubmit(): void {
    if (this.userForm.invalid) {
      this.formService.markFormGroupTouched(this.userForm);
      return;
    }
    if (this.userType === UserType.COOPERATION) {
      const cooperation: CooperationCreateUpdateModel = {
        id: this.user?.id ?? null,
        cooperation_name: this.userForm.value.cooperationName?.trim(),
        firstname: this.userForm.value.firstname?.trim(),
        lastname: this.userForm.value.lastname?.trim(),
        email: this.userForm.value.email?.trim(),
        phone_number: this.userForm.value.phone?.trim(),
        sponsor_cards: this.userForm.value.sponsorCards.map(sponsorCard => {
          return {
            id: sponsorCard.id ?? null,
            title: sponsorCard.title?.trim(),
            description: sponsorCard.description?.trim(),
            image: sponsorCard.image,
            link: sponsorCard.link?.trim(),
            active: sponsorCard.active !== false,
          };
        }),
      };
      this.editMode
        ? this.updateCooperation(cooperation)
        : this.createCooperation(cooperation);
    } else if (this.userType === UserType.LECTURER) {
      this.editMode ? this.updateLecturer() : this.createLecturer();
    } else {
      this.updateNormalUser();
    }
  }

  private updateNormalUser(): void {
    if (!this.editMode) {
      this.alertService.showErrorAlert(
        'Das Erstellen normaler Benutzer ist in diesem Formular nicht verfügbar.'
      );
      return;
    }

    const normalUser: UserUpdateModel = {
      id: this.user.id,
      salutation: this.user?.salutation ?? undefined,
      firstName: this.userForm.value.firstname?.trim(),
      lastName: this.userForm.value.lastname?.trim(),
      email: this.userForm.value.email?.trim(),
      bedMember: this.user?.bedMember ?? false,
      bedMemberNumber: this.user?.bedMemberNumber ?? undefined,
      birthdate: this.user?.birthdate ?? undefined,
      jobTitle: this.userForm.value.jobTitle?.trim(),
      phoneNumber: this.userForm.value.phone?.trim(),
      street: this.user?.address?.street ?? undefined,
      houseNumber: this.user?.address?.houseNumber ?? undefined,
      zipCode: this.user?.address?.zipCode ?? undefined,
      city: this.user?.address?.city ?? undefined,
      country: this.user?.address?.country ?? undefined,
      avatar: this.userForm.get('avatar').value,
    };

    this.userService
      .updateUser(normalUser)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.initialFormValues = this.userForm.value;
          this.alertService.showSuccessAlert(
            'Benutzer erfolgreich aktualisiert.'
          );
          this.router.navigate(['../../'], { relativeTo: this.route });
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Fehler beim Aktualisieren des Benutzers.'
          );
        },
      });
  }

  private createLecturer(): void {
    const lecturer: LecturerCreateModel = {
      job_title: this.userForm.value.jobTitle?.trim(),
      firstname: this.userForm.value.firstname?.trim(),
      lastname: this.userForm.value.lastname?.trim(),
      email: this.userForm.value.email?.trim(),
      phone_number: this.userForm.value.phone?.trim(),
      description1: this.userForm.value.description1?.trim(),
      description2: this.userForm.value.description2?.trim(),
      description3: this.userForm.value.description3?.trim(),
      field_of_activity: this.userForm.value.fieldOfActivity?.trim(),
      trainings: this.userForm.value.trainings?.filter(training => !!training),
      career_steps: this.userForm.value.careerSteps?.filter(
        careerStep => !!careerStep
      ),
      publications: this.userForm.value.publications?.filter(
        publication => !!publication.title
      ),
      engagements: this.userForm.value.engagements?.filter(
        engagement => !!engagement
      ),
      contributions: this.userForm.value.contributions.filter(
        contribution => !!contribution
      ),
      avatar: this.userForm.get('avatar').value,
    };

    this.userService.createLecturer(lecturer).subscribe(() => {
      this.initialFormValues = this.userForm.value;
      this.router.navigate(['../'], { relativeTo: this.route });
    });
  }

  private updateLecturer(): void {
    const lecturer: LecturerUpdateModel = {
      id: this.user.id,
      job_title: this.userForm.value.jobTitle?.trim(),
      firstname: this.userForm.value.firstname.trim(),
      lastname: this.userForm.value.lastname?.trim(),
      email: this.userForm.value.email?.trim(),
      phone_number: this.userForm.value.phone?.trim(),
      description1: this.userForm.value.description1?.trim(),
      description2: this.userForm.value.description2?.trim(),
      description3: this.userForm.value.description3?.trim(),
      field_of_activity: this.userForm.value.fieldOfActivity?.trim(),
      trainings: this.userForm.value.trainings?.filter(training => !!training),
      career_steps: this.userForm.value.careerSteps?.filter(
        careerStep => !!careerStep
      ),
      publications: this.userForm.value.publications?.filter(
        publication => !!publication.title
      ),
      engagements: this.userForm.value.engagements?.filter(
        engagement => !!engagement
      ),
      contributions: this.userForm.value.contributions.filter(
        contribution => !!contribution
      ),
      avatar: this.userForm.get('avatar').value,
    };

    this.userService
      .updateLecturer(lecturer)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.initialFormValues = this.userForm.value;
          this.router.navigate(['../../'], { relativeTo: this.route });
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Fehler beim Aktualisieren des Dozenten.'
          );
        },
      });
  }

  private createCooperation(cooperation: CooperationCreateUpdateModel): void {
    this.cooperationService
      .createCooperation(cooperation)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.initialFormValues = this.userForm.value;
          this.alertService.showSuccessAlert(
            'Kooperation erfolgreich erstellt.'
          );
          this.router.navigate(['../'], { relativeTo: this.route });
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Fehler beim Erstellen der Kooperation.'
          );
        },
      });
  }

  private updateCooperation(cooperation: CooperationCreateUpdateModel): void {
    this.cooperationService.updateCooperation(cooperation).subscribe({
      next: () => {
        this.initialFormValues = this.userForm.value;
        this.alertService.showSuccessAlert(
          'Kooperation erfolgreich aktualisiert.'
        );
        this.router.navigate(['../../'], { relativeTo: this.route });
      },
      error: () => {
        this.alertService.showErrorAlert(
          'Fehler beim Aktualisieren der Kooperation.'
        );
      },
    });
  }

  private async fetchUserNotes(userId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userService
        .getUserNotes(userId)
        .pipe(take(1))
        .subscribe(notes => {
          this.notes = notes.map(note => {
            return {
              ...note,
              userInitials: getUserInitials(note.user),
            };
          });
          resolve();
        });
    });
  }

  public onTabChange(tab: TabMenuTabModel): void {
    // add query param to url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        type: this.userType,
        tab: tab.key,
      },
    });
  }

  public onOpenUploadDialog() {
    this.fileInput.nativeElement.click();
  }

  public onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate the file type
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/webp',
      ];
      if (!allowedTypes.includes(file.type)) {
        this.alertService.showErrorAlert(
          'Ungültiger Dateityp. Bitte lade nur Bilder im Format JPG, PNG oder WEBP hoch.'
        );
        // remove the file from the input
        input.files = null;
        return;
      }

      this.openImageCropperDialog(event);
    }
  }

  private openImageCropperDialog(event: Event): void {
    const dialogRef = this.dialogService.open(ImageCropperDialogComponent, {
      header: 'Bild zuschneiden',
      width: '500px',
      breakpoints: {
        '640px': '90vw',
      },
      contentStyle: { overflow: 'auto' },
      data: {
        imageChangedEvent: event,
        aspectRatio: 1 / 1,
        roundCropper: true,
        height: 200,
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((croppedImage: string) => {
      if (croppedImage) {
        this.selectedImage = croppedImage;
        const croppedImageFile = base64ToFile(croppedImage, 'cropped-image');
        this.userForm.get('avatar')?.setValue(croppedImageFile);
      }
    });
  }

  /**
   * checks if the form has unsaved changes amd asks the user if he wants to leave the page
   * @returns CanDeactivateType
   */
  public canDeactivate(): CanDeactivateType {
    if (this.isLoading) {
      return true;
    }

    return this.formService.confirmDeactivation(
      this.userForm.value,
      this.initialFormValues
    );
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSponsorCardImageSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate the file type
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/webp',
      ];
      if (!allowedTypes.includes(file.type)) {
        this.alertService.showErrorAlert(
          'Ungültiger Dateityp. Bitte lade nur Bilder im Format JPG, PNG oder WEBP hoch.'
        );
        // remove the file from the input
        input.files = null;
        return;
      }

      resizeAndPadImage(file, 200, 10).then(processedImage => {
        // Create a new File object from the processed base64 image
        const processedFile = base64ToFile(processedImage, file.name);

        // Create a synthetic event with the processed image
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(processedFile);
        input.files = dataTransfer.files;

        this.openSponsorCardImageCropperDialog(event, index);
      });
    }
  }

  private openSponsorCardImageCropperDialog(event: Event, index: number): void {
    const dialogRef = this.dialogService.open(ImageCropperDialogComponent, {
      header: 'Bild zuschneiden',
      width: '500px',
      breakpoints: {
        '640px': '90vw',
      },
      contentStyle: { overflow: 'auto' },
      data: {
        imageChangedEvent: event,
        aspectRatio: 1 / 1,
        height: 200,
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((croppedImage: string) => {
      if (croppedImage) {
        this.sponsorCards.at(index).get('selectedImage').setValue(croppedImage);
        const croppedImageFile = base64ToFile(croppedImage, 'cropped-image');
        this.sponsorCards.at(index).get('image')?.setValue(croppedImageFile);
      }
    });
  }
}
